/**
 * ⚠️ Status: In Development - Adoption Guidelines
 *
 * While you can start adopting these new typography tokens, please be aware:
 * - Breaking changes may occur as we refine the system
 * - Component-level updates (buttons, inputs, etc.) are pending
 * - Some inconsistencies may exist during transition
 *
 * If adopting now:
 * - Test heavily in your specific use cases
 * - Refer to designs or ask design for intended usage
 * - Expect component-level updates for fixes
 * - Report any issues you encounter
 *
 * Recent changes:
 * - Added new typography system
 * - Added comprehensive documentation
 * - Component updates pending
 */

/**
 * Typography system for the P0 application.
 *
 * Usage guidelines:
 * - Headings (h1-h5): Used for section titles and content hierarchy
 *   Uses Lexend font for better readability and hierarchy
 *   Example: Page titles, section headers, subsection titles
 *
 * - Body: Used for main content text
 *   Uses Inter font for optimal reading experience
 *   Example: Paragraphs, descriptions, regular content
 *
 * - Small text variants: Used for supporting content
 *   Example: Captions, helper text, metadata
 *
 * Font sizes are in pixels, line heights in percentage
 */

export const typography = {
  // Font families
  fontFamily: {
    heading: "Lexend, -apple-system, BlinkMacSystemFont, sans-serif",
    body: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
    code: "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
  },

  // Heading styles
  h1: {
    fontSize: "40px",
    lineHeight: "160%",
    fontWeight: "bold",
    fontFamily: "Lexend, -apple-system, BlinkMacSystemFont, sans-serif",
  },
  h2: {
    fontSize: "32px",
    lineHeight: "160%",
    fontWeight: "bold",
    fontFamily: "Lexend, -apple-system, BlinkMacSystemFont, sans-serif",
  },
  h3: {
    fontSize: "24px",
    lineHeight: "160%",
    fontWeight: "bold",
    fontFamily: "Lexend, -apple-system, BlinkMacSystemFont, sans-serif",
  },
  h4: {
    fontSize: "20px",
    lineHeight: "160%",
    fontWeight: "bold",
    fontFamily: "Lexend, -apple-system, BlinkMacSystemFont, sans-serif",
  },
  h5: {
    fontSize: "16px",
    lineHeight: "160%",
    fontWeight: "bold",
    fontFamily: "Lexend, -apple-system, BlinkMacSystemFont, sans-serif",
  },

  // Body text styles
  body: {
    fontSize: "16px",
    lineHeight: "160%",
    fontWeight: "regular",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
  },
  bodySmall: {
    fontSize: "14px",
    lineHeight: "160%",
    fontWeight: "regular",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
  },
  bodyExtraSmall: {
    fontSize: "12px",
    lineHeight: "160%",
    fontWeight: "regular",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
  },

  // Font weights
  fontWeights: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
};

// Example usage:
// import { typography } from 'styles/typography';
//
// styled.h1`
//   font-size: ${typography.h1.fontSize};
//   line-height: ${typography.h1.lineHeight};
//   font-weight: ${typography.h1.fontWeight};
//   font-family: ${typography.fontFamily.heading};
// `;
